import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Link,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FunctionComponent } from "react";
import { RouteApp } from "../common/model/RouteApp";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import { forgotPassword } from "./redux/authAction";
import { Modal } from "../common/components/Modal";
import { RootState } from "../common/redux/store";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const ForgotPage: FunctionComponent = () => {
    const [errorMessage, setErrorMessage] = useState<any>(null);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

    const { t } = useTranslation();
    const authSelector: any = useAppSelector((state: RootState) => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const email = data.get("email")?.toString();

        if (!email) {
            setErrorMessage(t("forgot.password.error.empty"));
            return;
        }

        if (email.includes("@")) {
            dispatch(forgotPassword(email)).then((status: number) => {
                if (status === 429) {
                    setErrorMessage(t("forgot.password.error.tooManyRequests"));
                } else if (status === 404) {
                    setErrorMessage(t("forgot.password.error.emailNotFound"));
                } else if (status === 200) {
                    setIsOpenModal(true);
                    setErrorMessage(null);
                } else {
                    setErrorMessage(t("forgot.password.error"));
                }
            });
        } else {
            setErrorMessage(t("forgot.password.error.email"));
        }
    };

    useEffect(() => {
        if (authSelector.isSuccessPasswordRequest) {
            setIsOpenModal(true);
            setErrorMessage(null);
        } else if (authSelector.isErrorPasswordRequest) {
            setErrorMessage(t("forgot.password.error"));
            setIsOpenModal(false);
        }
    }, [
        authSelector.isErrorPasswordRequest,
        authSelector.isSuccessPasswordRequest,
        t,
    ]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100dvh",
                width: "100%",
            }}
        >
            <Paper
                elevation={2}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: "440px",
                    padding: "10px",
                    margin: "5px",
                }}
            >
                <Typography variant="h5">
                    {t("forgot.password.title")}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                >
                    {t("forgot.password.subtitle")}
                </Typography>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ mt: 1 }}
                >
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t("auth.email")}
                        name="email"
                        autoComplete="email"
                        autoFocus
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="error"
                        sx={{ mt: 2, mb: 2 }}
                    >
                        {t("forgot.password.reset")}
                        {authSelector.isLoader && (
                            <CircularProgress
                                size={18}
                                color="inherit"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Button>
                    {errorMessage && (
                        <Alert
                            severity="error"
                            className="errorMessage"
                            sx={{
                                width: "100%",
                                maxWidth: "420px",
                                boxSizing: "border-box",
                                fontSize: "12px",
                            }}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                    <Grid container>
                        <Grid item>
                            <Link href={RouteApp.LOGIN} variant="body2">
                                {t("forgot.password.login")}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
            <Modal
                open={isOpenModal}
                message={t("forgot.password.modal")}
                title={t("forgot.password.title.modal")}
                onClose={() => setIsOpenModal(false)}
                onValidation={() => {
                    setIsOpenModal(false);
                    navigate(RouteApp.LOGIN);
                }}
                titleButtonValidation={t("forgot.password.button.modal")}
            ></Modal>
        </div>
    );
};
